/**
 * What is specific to the admin of the vitrine (docs/specs/vitrine.md sections 6
 * and 7, issue #192) - pure, so it is tested without a router or a server.
 *
 * The role itself comes from `lib/use-instance.tsx`; this file holds what the
 * screens DO with it: which paths carry the banner, which paths do not exist
 * on a vitrine, where the banner's button leads, and the words.
 */

/**
 * The screens whose content is a MODEL (section 7): the ones that carry the
 * banner « Vitrine - on n'y modifie rien ». Sessions, Entretiens and Partage are
 * not in it, and that is the point of the list: they are written on the
 * vitrine (section 6), and the sentence would be false there.
 *
 * A prefix is a whole segment: `/admin/experiences` covers
 * `/admin/experiences/abc` and not `/admin/experiences-x`.
 *
 * Méthode (issue #196, analyste.md section 4.4) is the ninth: a third setting
 * that reads on the vitrine without a gesture, beside Modèles, Règles and Noyaux
 * (section 7: « et Méthode depuis #196 »).
 */
export const VITRINE_MODEL_PREFIXES: readonly string[] = [
  '/admin/experiences',
  '/admin/protocoles',
  '/admin/criteres',
  '/admin/personas',
  '/admin/mediatheque',
  '/admin/reglages/modeles',
  '/admin/reglages/regles',
  '/admin/reglages/noyaux',
  '/admin/reglages/methode',
];

function under(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/** Does this path show a model, which the vitrine only lets one read? */
export function isVitrineModelScreen(pathname: string): boolean {
  return VITRINE_MODEL_PREFIXES.some((prefix) => under(pathname, prefix));
}

/** The destinations a vitrine sends a path to that does not exist on it. */
export type VitrineRedirect =
  | '/admin/sessions'
  | '/admin/reglages/modeles'
  | '/admin/criteres'
  | '/admin/experiences'
  | '/admin/protocoles'
  | '/admin/personas';

/**
 * Where a path that has no place on the vitrine goes, or null when it has one.
 *
 * Three tabs of Réglages are gone: Jetons (without the assistant a personal
 * token serves nothing, H6), Personas (the library of tones and levels only
 * serves to write a persona, H1) and Rejeu (it exists on the atelier alone).
 * And the four « nouveau » entries lead to an empty editor that could only be
 * refused: a card « Créer » is not drawn, and the address does not stay open
 * either. The server answers the same paths 404 or 403 on its side; this is
 * what the screen does so that nobody lands on the refusal.
 *
 * And « État de la prod » (§9.3, lot F): it is the atelier's - it reads what the
 * atelier has ahead of the prod - and the vitrine has no such route. It goes to
 * the home of the admin, Sessions, where `/admin` itself lands.
 */
export function vitrineRedirect(pathname: string): VitrineRedirect | null {
  if (
    under(pathname, '/admin/reglages/jetons') ||
    under(pathname, '/admin/reglages/personas') ||
    under(pathname, '/admin/reglages/rejeu')
  ) {
    return '/admin/reglages/modeles';
  }
  if (under(pathname, '/admin/prod')) return '/admin/sessions';
  // The NPC, the benches and the series are the atelier's alone (npc.md H2, N5): the vitrine has
  // none, and its server answers their routes 404. `/admin/npcx` is not under it.
  if (under(pathname, '/admin/npc')) return '/admin/sessions';
  if (pathname === '/admin/criteres/nouveau') return '/admin/criteres';
  if (pathname === '/admin/experiences/nouveau') return '/admin/experiences';
  if (pathname === '/admin/protocoles/nouveau') return '/admin/protocoles';
  if (pathname === '/admin/personas/nouveau') return '/admin/personas';
  return null;
}

/**
 * The same path on the atelier (section 7: « le même objet »), or null when the
 * vitrine was not told where the atelier is.
 *
 * The path and the search travel, nothing else: a `?version=4` opens the same
 * version over there, and the atelier asks its own operator to sign in.
 */
export function atelierHref(
  atelierOrigin: string | null,
  pathname: string,
  searchStr: string,
): string | null {
  if (atelierOrigin === null) return null;
  const origin = atelierOrigin.endsWith('/') ? atelierOrigin.slice(0, -1) : atelierOrigin;
  const search = searchStr === '' || searchStr.startsWith('?') ? searchStr : `?${searchStr}`;
  return `${origin}${pathname}${search}`;
}

/** The mark of the top bar: « Vitrine · v1.3.0 ». */
export function vitrineMark(code: string): string {
  return `Vitrine · ${code}`;
}

/** The words of the vitrine, in one place: a second spelling would be a second meaning. */
export const VITRINE_WORDING = {
  bannerTitle: 'Vitrine - on n’y modifie rien.',
  bannerRest: 'Les modifications se font sur form.dev.',
  openAtelier: 'Ouvrir sur form.dev',
  /** What replaces the dry trials of a session (section 7). */
  trialsNote: 'Les essais à blanc se font sur form.dev, sur la session remontée.',
  openSessionAtelier: 'Ouvrir la session sur form.dev',
  trialsHint:
    'La session remonte à la synchronisation de chaque heure ; « Synchroniser », sur form.dev, la fait remonter tout de suite.',
  /** The history of a version that the vitrine plays. */
  playedByLinks: 'la dernière, jouée par les liens',
  historyHint: 'Les versions présentes sur la prod. Seul geste : « Voir ».',
  backToLatest: 'Revenir à la dernière version',
  /** The line under a card: the version arrived from form.dev. */
  arrived: (number: number, day: string, author: string) =>
    `v${number} arrivée sur la prod le ${day} · ${author}`,
} as const;
